import { forwardRef, useEffect, useRef, useState } from 'react';
import { styled } from '../../stitches.config';
import { IconButton, IconButtonProps } from '../IconButton';
import { Flex } from '../Flex';
import { useMergeRefs } from '../..';

export const StyledPagination = styled('div', {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: 4,
});

export interface PaginationProps
  extends Omit<React.ComponentProps<typeof StyledPagination>, 'onClick'> {
  as?: React.ElementType;
  currentPage: number;
  totalPages: number;
  getHref: (page: number) => string;
  onClick: ({ page, href }: { page: number; href: string }) => void;
  defaultMaxButtons?: number;
  size?: IconButtonProps['size'];
}

export interface PaginationButtonProps extends Omit<IconButtonProps, 'label'> {
  page: number;
}

export const PaginationButton = forwardRef<
  HTMLButtonElement,
  PaginationButtonProps
>(({ page, ...rest }, ref) => {
  return (
    <IconButton ref={ref} label={`Page ${page}`} tabIndex={-1} {...rest}>
      {page}
    </IconButton>
  );
});

export const Ellipsis = () => {
  return (
    <Flex justify='center' css={{ width: 40, height: 40 }}>
      <span style={{ paddingBottom: 8 }}>...</span>
    </Flex>
  );
};

export const generatePaginationNumbers = ({
  currentPage,
  totalPages,
  maxButtons,
}: {
  currentPage: number;
  totalPages: number;
  maxButtons: number;
}) => {
  const pages: (number | string)[] = [];
  // reserved previous and next
  const defaultReservedButtonCount = 2;
  const showEllipsisEnd = totalPages > maxButtons - defaultReservedButtonCount;

  // add 2 for first and last page
  let reservedButtonCount = defaultReservedButtonCount + 2;
  // add one if showEllipsisEnd
  reservedButtonCount += showEllipsisEnd ? 1 : 0;
  // Always show first page
  pages.push(1);

  // if current page is less then the max buttons minus reserved buttons
  if (currentPage <= maxButtons - reservedButtonCount) {
    // Show continuous sequence from start. we subtract 1 because it is already accounted for in start of the loop
    for (
      let i = 2;
      i <= Math.min(maxButtons - (reservedButtonCount - 1), totalPages - 1);
      i++
    ) {
      pages.push(i);
    }
    if (showEllipsisEnd) pages.push('...');
  } else if (currentPage > totalPages - (maxButtons - reservedButtonCount)) {
    // Show continuous sequence from end
    if (showEllipsisEnd) pages.push('...');
    for (
      let i = totalPages - (maxButtons - reservedButtonCount);
      i < totalPages;
      i++
    ) {
      pages.push(i);
    }
  } else {
    // add 2 for one more ellipsis and the current page
    reservedButtonCount += 2;
    // Show current page with surrounding pages
    pages.push('...');
    const offset = Math.floor((maxButtons - reservedButtonCount) / 2);
    for (let i = currentPage - offset; i <= currentPage + offset; i++) {
      pages.push(i);
    }
    pages.push('...');
  }

  /**
   * here we check if we have room to add another button.
   * this only happens if because of rounding. so we at most will need to add one more button.
   * we add one when the current pages legnth is less than maxButtons minus reserved buttons (2 for previous and next) and minus one for last page
   * AND the total pages is greater than maxButtons minus reserved buttons (2 for previous and next)
   */
  if (
    pages.length < maxButtons - defaultReservedButtonCount - 1 &&
    showEllipsisEnd
  ) {
    pages.push(totalPages - 1);
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      defaultMaxButtons,
      onClick,
      getHref,
      size,
      ...rest
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs(containerRef, ref);
    const [maxButtons, setMaxButtons] = useState(defaultMaxButtons ?? 7);

    useEffect(() => {
      // maxButtons have been set by the user, bail
      if (defaultMaxButtons) {
        return;
      }

      const calculateMaxButtons = () => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const availableWidth = containerWidth - 8;
        const buttonsCount = Math.floor(availableWidth / 40);
        // space for 7 buttons is required
        setMaxButtons(Math.max(buttonsCount, 7));
      };

      calculateMaxButtons();

      const resizeObserver = new ResizeObserver(calculateMaxButtons);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    }, [containerRef]);

    return (
      <StyledPagination
        role='navigation'
        aria-label='Pagination'
        ref={combinedRef}
        {...rest}
      >
        <a
          href={getHref(currentPage - 1)}
          aria-label={`Go to previous page`}
          rel='prev'
          onClick={(e) => {
            e.preventDefault();
            onClick({ page: currentPage - 1, href: getHref(currentPage - 1) });
          }}
          aria-disabled={currentPage <= 1}
        >
          <IconButton
            variant='darkGray'
            name='arrow-left'
            label='Go to previous page'
            size={size}
            disabled={currentPage <= 1}
          />
        </a>
        {generatePaginationNumbers({ currentPage, totalPages, maxButtons }).map(
          (page, index) => {
            if (typeof page === 'string') {
              return <Ellipsis key={`ellipsis-${index}`} />;
            }

            const href = getHref(page);
            return (
              <a
                key={page}
                href={href}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page}
                onClick={(e) => {
                  e.preventDefault();
                  onClick({ page, href });
                }}
              >
                <PaginationButton
                  variant={currentPage === page ? 'purple-secondary' : 'ghost'}
                  page={page}
                  size={size}
                />
              </a>
            );
          },
        )}
        <a
          href={getHref(currentPage + 1)}
          aria-label={`Go to next page`}
          rel='next'
          onClick={(e) => {
            e.preventDefault();
            onClick({ page: currentPage + 1, href: getHref(currentPage + 1) });
          }}
          aria-disabled={currentPage >= totalPages}
        >
          <IconButton
            variant='darkGray'
            name='arrow-right'
            label='Go to next page'
            size={size}
            disabled={currentPage >= totalPages}
          />
        </a>
      </StyledPagination>
    );
  },
);

Pagination.displayName = 'Pagination';
